import React, { useState, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useServices } from '../context/ServiceContext';
import { useCompany } from '../context/CompanyContext';
import './ToolsPanel.css';

const TOOLS = [
  { id:'services',  icon:'🏷️', label:'Service Types',     desc:'Manage your service catalogue' },
  { id:'import',    icon:'📥', label:'Import Leads',       desc:'Upload CSV file' },
  { id:'export',    icon:'📤', label:'Export Leads',       desc:'Download as CSV' },
  { id:'exportall', icon:'📊', label:'Export All Data',    desc:'Full data backup JSON' },
  { id:'notes',     icon:'📝', label:'Quick Notes',        desc:'Scratch pad for ideas' },
  { id:'invoice',   icon:'🧾', label:'Invoice Generator',  desc:'Create & print invoices' },
];

function ServiceTypes({ theme }) {
  const { services, add, remove, edit } = useServices();
  const [newSvc, setNewSvc] = useState('');
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState('');

  return (
    <div className="tp-section">
      <div className="tp-sec-title">Service Types <span className="tp-badge">{services.length}</span></div>
      <div className="tp-svc-list">
        {services.map(s => (
          <div key={s} className="tp-svc-item">
            {editing === s ? (
              <input className="tp-svc-inp" value={editVal} autoFocus
                onChange={e=>setEditVal(e.target.value)}
                onBlur={()=>{edit(s,editVal);setEditing(null);}}
                onKeyDown={e=>{if(e.key==='Enter'){edit(s,editVal);setEditing(null);}if(e.key==='Escape')setEditing(null);}} />
            ) : (
              <span className="tp-svc-name" onClick={()=>{setEditing(s);setEditVal(s);}}>{s}</span>
            )}
            <div className="tp-svc-actions">
              <div className="tp-svc-btn" onClick={()=>{setEditing(s);setEditVal(s);}}>✏</div>
              <div className="tp-svc-btn tp-svc-del" onClick={()=>remove(s)}>✕</div>
            </div>
          </div>
        ))}
      </div>
      <div className="tp-svc-add-row">
        <input className="tp-svc-add-inp" placeholder="Add new service type…" value={newSvc}
          onChange={e=>setNewSvc(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter'){add(newSvc);setNewSvc('');}}} />
        <div className="tp-svc-add-btn" onClick={()=>{add(newSvc);setNewSvc('');}}>+ Add</div>
      </div>
    </div>
  );
}

function ImportLeads({ theme }) {
  const fileRef = useRef();
  const [status, setStatus] = useState('');
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setStatus(`✓ "${file.name}" selected — import ready (connects to backend)`);
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Import Leads</div>
      <div className="tp-upload-zone" onClick={()=>fileRef.current.click()}>
        <span className="tp-upload-icon">📥</span>
        <div className="tp-upload-label">Click to select CSV file</div>
        <div className="tp-upload-hint">Columns: Name, Company, Phone, Email, Source, Status</div>
        <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={handleFile}/>
      </div>
      {status && <div className="tp-status">{status}</div>}
      <a className="tp-template-link" href="data:text/csv,Name,Company,Phone,Email,Source,Status,Value%0AExample Lead,Example Corp,+91 99999 88888,example@corp.com,Website,New,50000" download="leads_template.csv">
        ↓ Download CSV template
      </a>
    </div>
  );
}

function ExportLeads() {
  const handleExport = () => {
    const csv = [
      'Name,Company,Phone,Email,Source,Status,Value',
      'Anand Kumar,Techwave Solutions,+91 98765 43210,anand@techwave.in,Meta Ads,Hot,45000',
      'Priya Sharma,Bloom Studio,+91 87654 32109,priya@bloomstudio.in,Website,New,18000',
      'Rajesh Nair,Nair Exports,+91 76543 21098,rajesh@nairexports.com,Referral,Warm,72000',
    ].join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download='leads_export.csv'; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Export Leads</div>
      <div className="tp-export-info">Downloads all leads as a CSV file you can open in Excel or Google Sheets.</div>
      <div className="tp-export-btn" onClick={handleExport}>📤 Download Leads CSV</div>
    </div>
  );
}

function ExportAll() {
  const handleExport = () => {
    const data = {
      exported: new Date().toISOString(),
      leads: 10, invoices: 8, team: 12, projects: 10,
      note: 'Full export connects to backend in production'
    };
    const blob = new Blob([JSON.stringify(data,null,2)], {type:'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download='ryzenos_backup.json'; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Export All Data</div>
      <div className="tp-export-info">Full data backup including leads, invoices, team, and projects.</div>
      <div className="tp-export-btn" onClick={handleExport}>💾 Download Full Backup</div>
    </div>
  );
}

function QuickNotes() {
  const [notes, setNotes] = useState('');
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Quick Notes</div>
      <textarea className="tp-notes" placeholder="Jot anything down…" value={notes} onChange={e=>setNotes(e.target.value)} rows={8}/>
      <div className="tp-notes-hint">Notes are saved locally in this session.</div>
    </div>
  );
}


function InvoiceGenerator({ theme }) {
  const { company } = useCompany();
  const [form, setForm] = React.useState({
    invoiceNo: 'INV-' + String(Date.now()).slice(-6),
    date: new Date().toISOString().split('T')[0],
    clientName:'', clientCompany:'', clientAddress:'',
    service:'', description:'', amount:'', gst:'18',
  });
  const setF = (k,v) => setForm(p=>({...p,[k]:v}));
  const subtotal = parseFloat(form.amount)||0;
  const gstAmt   = subtotal * (parseFloat(form.gst)||0)/100;
  const total    = subtotal + gstAmt;
  const fmtINR   = v => '₹'+v.toLocaleString('en-IN');

  const handlePrint = () => {
    const win = window.open('','_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>Invoice ${form.invoiceNo}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Plus Jakarta Sans,sans-serif;padding:40px;color:#0f0c08}
    .hd{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;padding-bottom:16px;border-bottom:2px solid #F5A623}
    .logo{font-size:22px;font-weight:900;color:#F5A623}.inv-title{font-size:20px;font-weight:700}
    .inv-meta{font-size:12px;color:#666;margin-top:4px}
    .parties{display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-bottom:28px}
    .party-lbl{font-size:9px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#999;margin-bottom:6px}
    .party-name{font-size:14px;font-weight:700;margin-bottom:3px}
    .party-detail{font-size:12px;color:#666;line-height:1.6}
    table{width:100%;border-collapse:collapse;margin-bottom:20px}
    th{text-align:left;padding:9px 12px;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;background:#f9f7f3;color:#666}
    td{padding:11px 12px;font-size:13px;border-bottom:0.5px solid #eee}
    .totals{margin-left:auto;width:260px}
    .tot-row{display:flex;justify-content:space-between;padding:7px 0;font-size:13px;border-bottom:0.5px solid #eee}
    .tot-total{font-size:16px;font-weight:800;color:#F5A623}
    .footer{margin-top:40px;padding-top:16px;border-top:0.5px solid #eee;font-size:11px;color:#999;text-align:center}
    </style></head><body>
    <div class="hd">
      <div><div class="logo">${company.name||'Your Company'}</div>
        <div style="font-size:12px;color:#666;margin-top:4px">${company.address||''}, ${company.city||''} ${company.pincode||''}</div>
        <div style="font-size:12px;color:#666">${company.phone||''} · ${company.email||''}</div>
        ${company.gstin?'<div style="font-size:11px;color:#999">GSTIN: '+company.gstin+'</div>':''}
      </div>
      <div style="text-align:right">
        <div class="inv-title">INVOICE</div>
        <div class="inv-meta"># ${form.invoiceNo}</div>
        <div class="inv-meta">Date: ${new Date(form.date).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>
      </div>
    </div>
    <div class="parties">
      <div><div class="party-lbl">From</div><div class="party-name">${company.name||'Your Company'}</div><div class="party-detail">${company.tagline||''}</div></div>
      <div><div class="party-lbl">Bill To</div><div class="party-name">${form.clientName||'Client Name'}</div><div class="party-detail">${form.clientCompany||''}<br/>${form.clientAddress||''}</div></div>
    </div>
    <table><thead><tr><th>Description</th><th>Service</th><th style="text-align:right">Amount</th></tr></thead>
    <tbody><tr><td>${form.description||'—'}</td><td>${form.service||'—'}</td><td style="text-align:right;font-weight:700">${fmtINR(subtotal)}</td></tr></tbody></table>
    <div class="totals">
      <div class="tot-row"><span>Subtotal</span><span>${fmtINR(subtotal)}</span></div>
      <div class="tot-row"><span>GST (${form.gst}%)</span><span>${fmtINR(gstAmt)}</span></div>
      <div class="tot-row tot-total"><span>Total</span><span>${fmtINR(total)}</span></div>
    </div>
    <div class="footer">Thank you for your business · ${company.email||''} · ${company.website||''}</div>
    </body></html>`);
    win.document.close();
    win.print();
  };

  return (
    <div className="tp-section">
      <div className="tp-sec-title">Invoice Generator</div>
      <div className="tp-inv-two">
        <div className="tp-inv-form">
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Invoice #</label><input className="tp-inv-inp" value={form.invoiceNo} onChange={e=>setF('invoiceNo',e.target.value)}/></div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Date</label><input className="tp-inv-inp" type="date" value={form.date} onChange={e=>setF('date',e.target.value)}/></div>
          <div className="tp-inv-sec">Bill To</div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Client name</label><input className="tp-inv-inp" placeholder="John Doe" value={form.clientName} onChange={e=>setF('clientName',e.target.value)}/></div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Company</label><input className="tp-inv-inp" placeholder="Company Ltd" value={form.clientCompany} onChange={e=>setF('clientCompany',e.target.value)}/></div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Address</label><input className="tp-inv-inp" placeholder="City, State" value={form.clientAddress} onChange={e=>setF('clientAddress',e.target.value)}/></div>
          <div className="tp-inv-sec">Service</div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Title</label><input className="tp-inv-inp" placeholder="e.g. Website Development" value={form.service} onChange={e=>setF('service',e.target.value)}/></div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Description</label><input className="tp-inv-inp" placeholder="Brief description" value={form.description} onChange={e=>setF('description',e.target.value)}/></div>
          <div className="tp-inv-row">
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Amount (₹)</label><input className="tp-inv-inp" type="number" placeholder="0" value={form.amount} onChange={e=>setF('amount',e.target.value)}/></div>
            <div className="tp-inv-grp"><label className="tp-inv-lbl">GST %</label><input className="tp-inv-inp" type="number" placeholder="18" value={form.gst} onChange={e=>setF('gst',e.target.value)}/></div>
          </div>
          <div className="tp-inv-total-row">
            <span>Total</span><span className="tp-inv-total">₹{total.toLocaleString('en-IN')}</span>
          </div>
          <div className="tp-export-btn" style={{marginTop:8}} onClick={handlePrint}>🖨 Print / Download PDF</div>
        </div>
      </div>
    </div>
  );
}

export default function ToolsPanel({ onClose }) {
  const { theme } = useTheme();
  const [active, setActive] = useState('services');

  const renderContent = () => {
    if (active==='services')  return <ServiceTypes theme={theme}/>;
    if (active==='import')    return <ImportLeads theme={theme}/>;
    if (active==='export')    return <ExportLeads/>;
    if (active==='exportall') return <ExportAll/>;
    if (active==='notes')     return <QuickNotes/>;
    if (active==='invoice')   return (
      <div className="tp-section">
        <div className="tp-sec-title">Invoice Generator</div>
        <div className="tp-export-info">Create and manage invoices in the Finance module.</div>
        <div className="tp-export-btn" onClick={()=>{window.location.href='/finance';onClose();}}>🧾 Go to Finance</div>
      </div>
    );
    return null;
  };

  return (
    <div className="tp-wrap" data-theme={theme}>
      <div className="tp-overlay" onClick={onClose}/>
      <div className="tp-panel" data-theme={theme}>
        <div className="tp-header">
          <span className="tp-title">Tools</span>
          <div className="tp-close" onClick={onClose}>✕</div>
        </div>
        <div className="tp-body">
          <div className="tp-nav">
            {TOOLS.map(t=>(
              <div key={t.id} className={`tp-nav-item${active===t.id?' active':''}`} onClick={()=>setActive(t.id)}>
                <span className="tp-nav-icon">{t.icon}</span>
                <div>
                  <div className="tp-nav-label">{t.label}</div>
                  <div className="tp-nav-desc">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="tp-content">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
}
